/**
 * Product limits (02-requirements.md). Constants, not admin settings.
 * HANDOFF D7: changing any value away from the spec needs the owner.
 */

// ---- money (R-LST-1, ADR-A15, ADR-A17)
export const CURRENCY = "VND" as const;
export const PRICE_STEP = 1_000;
export const MIN_START_PRICE = 10_000;
export const MAX_START_PRICE = 10_000_000_000; // sanity cap, 10 tỷ
export const MAX_FLOOR_RATIO = 0.9;

// ---- durations (R-LST-2)
export const DURATIONS = {
  h6: 21_600,
  h24: 86_400,
  d3: 259_200,
  d7: 604_800,
} as const;
export type DurationKey = keyof typeof DURATIONS;
export const DURATION_VALUES: readonly number[] = Object.values(DURATIONS);
export const DROP_ONLY_DURATION_S = DURATIONS.h6; // only inside a Drop window

// ---- Drop (R-DRP-1, R-LST-3, ADR-A03) — Asia/Ho_Chi_Minh is UTC+7, no DST
export const TZ_OFFSET_S = 7 * 3600;
export const DROP_DOW = 6; // Saturday (0 = Sunday)
export const DROP_LOCAL_TIME_S = 20 * 3600; // 20:00
export const DROP_WINDOW_S = 86_400; // 6 h preset allowed Sat 20:00 → Sun 20:00
export const DROP_MIN_LEAD_S = 2 * 3600; // within 2 h before a Drop → the one after
export const DROP_PERIOD_S = 7 * 86_400;
export const DROP_HOME_LEAD_S = 86_400; // home page leads with "This week's Drop" for 24 h
export const DROP_REMINDER_LEAD_S = 3_600; // T−1 h reminder

// ---- time buckets (R-BRW-5), by seconds remaining
export const BUCKET_ENDING_SOON_S = 3_600;
export const BUCKET_HOURS_S = 21_600;
export const BUCKET_UNDER_DAY_S = 86_400;

// ---- polling / interpolation (R-BRW-6)
export const INTERPOLATE_MAX_S = 60;
export const SEGMENT_POLL_LISTING_S = 15;
export const SEGMENT_POLL_CARDS_S = 30;
export const SEGMENT_EDGE_CACHE_S = 5;
export const SEGMENTS_MAX_IDS = 48;

// ---- accounts (R-ACC-*)
export const PHONE_E164 = /^\+[1-9]\d{7,14}$/;
export const OTP_LENGTH = 6;
export const OTP_TTL_S = 300;
export const OTP_MAX_ATTEMPTS = 5;
export const OTP_SENDS_PER_PHONE = 3;
export const OTP_SENDS_PER_IP = 10;
export const OTP_RATE_WINDOW_S = 900;
export const OTP_LOCKOUT_S = 3_600;
export const OTP_RESEND_COOLDOWN_S = 60;
export const SESSION_TTL_S = 90 * 86_400;
export const SESSION_TOKEN_BYTES = 32;
export const DISPLAY_NAME_MIN = 2;
export const DISPLAY_NAME_MAX = 30;
export const HANDLE_RE = /^[a-z0-9_]{3,20}$/;
export const HANDLE_CHANGE_COOLDOWN_S = 90 * 86_400;
export const HANDLE_RESERVED: readonly string[] = [
  "admin",
  "yabe",
  "me",
  "api",
  "support",
  "help",
  "root",
  "system",
  "null",
  "undefined",
];
export const DELETED_HANDLE = "[đã xoá]";
export const DELETION_ANONYMISE_AFTER_S = 14 * 86_400;

// ---- invites (R-INV-*)
export const INVITE_POOL = 3;
export const INVITE_CODE_LENGTH = 8;
export const INVITE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const INVITE_BATCH_MAX = 500;

// ---- listings (R-LST-*)
export const TITLE_MIN = 5;
export const TITLE_MAX = 80;
export const DESCRIPTION_MAX = 2_000;
export const QUANTITY_MIN = 1;
export const QUANTITY_MAX = 20;
export const IMAGES_MIN = 1;
export const IMAGES_MAX = 12;
export const IMAGE_MAX_BYTES = 300 * 1024;
export const IMAGE_MAX_SIDE_PX = 2_000;
export const IMAGE_UPLOAD_URL_TTL_S = 300;
export const DRAFTS_MAX = 10;
export const LISTING_CAP_DEFAULT = 30;
export const CONDITIONS = ["new", "like_new", "good", "fair"] as const;
export type Condition = (typeof CONDITIONS)[number];
export const START_MODES = ["now", "drop"] as const;
export type StartMode = (typeof START_MODES)[number];

// ---- browse (R-BRW-*)
export const PAGE_SIZE = 24;
export const SEARCH_QUERY_MAX = 80;
export const BROWSE_SORTS = ["default", "newest", "price_asc", "price_desc"] as const;

// ---- watches / alerts (R-ALT-*, R-LST-9)
export const ALERT_LADDER_MIN_ALERTS = 3;
export const ALERT_LADDER_BUCKET_RATIO = 0.05;
export const WATCHING_HIDE_ENDED_AFTER_S = 7 * 86_400;

// ---- buying / completion (R-BUY-*, R-CMP-*)
export const BUY_MAX_UNITS = 10;
export const OPEN_PURCHASES_MAX = 3;
export const CONTACT_SHARE_DEADLINE_S = 86_400; // ADR-A09
export const AUTO_SHARE_MIN_COMPLETED = 3;
export const DEAL_CODE_LENGTH = 4;
export const DEAL_CODE_MAX_ATTEMPTS = 5;
export const DEAL_CODE_LOCK_S = 3_600;
export const DONE_CONFIRM_WINDOW_S = 48 * 3_600;
export const AUTO_COMPLETE_AFTER_S = 7 * 86_400;
export const PROBLEM_WINDOW_AFTER_COMPLETE_S = 7 * 86_400;
export const PROBLEM_NOTE_MAX = 1_000;
export const PROBLEM_IMAGES_MAX = 3;
export const PROBLEM_IMAGE_URL_TTL_S = 600;
export const BUYER_REASONS = ["seller_no_show", "item_not_as_described", "other"] as const;
export const SELLER_REASONS = ["buyer_no_show", "payment_not_received", "other"] as const;
export const PROBLEM_RESOLUTIONS = [
  "no_action",
  "strike_buyer",
  "strike_seller",
  "cancel_purchase",
  "complete_purchase",
] as const;

// ---- trust (R-TRS-*)
export const STRIKE_TTL_S = 90 * 86_400;
export const STRIKES_TO_SUSPEND = 3;
export const STRIKE_REASONS = ["buyer_no_show", "seller_no_show", "problem_upheld"] as const;
export const REPORT_REASONS = ["prohibited", "counterfeit", "misleading", "spam", "other"] as const;
export const REPORT_NOTE_MAX = 500;
export const REPORTS_TO_AUTO_HIDE = 3;
export const SELLER_CANCEL_WINDOW_S = 90 * 86_400;
export const SELL_THROUGH_WINDOW_S = 180 * 86_400;
export const SELL_THROUGH_MIN_PUBLISHED = 3;

// ---- notifications (R-NTF-*)
export const NOTIFICATIONS_PRUNE_AFTER_S = 90 * 86_400;
export const NOTIFICATIONS_PAGE = 30;
export const TG_LINK_TOKEN_TTL_S = 600;
export const TELEGRAM_TYPES = [
  "purchase_received",
  "contact_shared",
  "alert_reached",
  "watch_ending_soon",
  "drop_reminder",
  "drop_start",
  "problem_filed",
  "problem_replied",
  "problem_resolved",
  "strike_issued",
  "done_requested",
] as const;

// ---- misc
export const VIEWS_KV_TTL_S = 2 * 86_400;
export const DROP_NEXT_CACHE_S = 60;
export const ADMIN_NOTE_MAX = 1_000;
export const EMAIL_MAX = 254;
